/**
 * Sanction — Preorder Badge
 *
 * Self-contained IIFE that queries the Storefront API for per-variant
 * preorder metafields and dynamically manages the product page UI.
 *
 * What it does:
 *   1. On product pages, queries the Storefront API for per-variant
 *      `custom.preorder` metafield values.
 *   2. When the selected variant has preorder = "true", shows an
 *      amber "Pre-order" badge on the page.
 *   3. Changes the ATC button text to a configurable preorder label.
 *   4. Shows a configurable ship-date message below the badge.
 *   5. Injects a hidden `_preorder` line item property for order tagging.
 *   6. Hides dynamic payment buttons (Shop Pay, Apple Pay, etc.).
 *   7. Hides the pickup availability section.
 *
 * Requirements:
 *   - A Storefront API access token with product read scope.
 *   - A <script id="preorder-config" type="application/json"> block
 *     rendered by the Liquid snippet (Task 5) containing shipMessage,
 *     preorderText, and addToCartText.
 */

(function () {
  'use strict';

  // ─── Configuration ──────────────────────────────────────────────
  var SANCTION_PO_CONFIG = {
    storefrontToken: '85d23c7453cf7964ec58c9f3ada2586c',
    shopDomain: 'sanction.myshopify.com',
    apiVersion: '2025-01',
  };
  // ─── End Configuration ──────────────────────────────────────────

  // ── Liquid Config (rendered by snippet) ─────────────────────────
  var configEl = document.getElementById('preorder-config');
  var liquidConfig = configEl ? JSON.parse(configEl.textContent) : {};
  var PREORDER_TEXT = liquidConfig.preorderText || 'Pre-order';
  var SHIP_MESSAGE = liquidConfig.shipMessage || '';

  // ── Guards ──────────────────────────────────────────────────────
  var pathname = window.location.pathname;
  var productMatch = pathname.match(/\/products\/([^/?#]+)/);
  if (!productMatch) return;

  var productHandle = productMatch[1];

  // ── State ───────────────────────────────────────────────────────
  // { variantId: { preorder: boolean, purchasable: boolean } }
  var preorderMap = {};
  var dataLoaded = false;
  var badgeElement = null;
  var shipMessageElement = null;
  var pickupObserver = null;

  // ── Storefront API Query ────────────────────────────────────────
  var QUERY = [
    'query ProductPreorderStatus($handle: String!) {',
    '  product(handle: $handle) {',
    '    variants(first: 100) {',
    '      edges {',
    '        node {',
    '          id',
    '          title',
    '          availableForSale',
    '          metafield(namespace: "custom", key: "preorder") {',
    '            value',
    '          }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  // ── Helpers ─────────────────────────────────────────────────────

  function numericId(gid) {
    var parts = gid.split('/');
    return parts[parts.length - 1];
  }

  function getSelectedVariantId() {
    var params = new URLSearchParams(window.location.search);
    var fromUrl = params.get('variant');
    if (fromUrl) return fromUrl;

    if (
      window.ShopifyAnalytics &&
      window.ShopifyAnalytics.meta &&
      window.ShopifyAnalytics.meta.selectedVariantId
    ) {
      return String(window.ShopifyAnalytics.meta.selectedVariantId);
    }

    try {
      var productJson = document.querySelector('[data-product-json], script[type="application/json"][data-product-json]');
      if (productJson) {
        var data = JSON.parse(productJson.textContent);
        if (data && data.variants && data.variants.length) {
          return String(data.variants[0].id);
        }
      }
    } catch (_) {}

    var keys = Object.keys(preorderMap);
    return keys.length ? keys[0] : null;
  }

  function isPreorder(variantId) {
    if (!dataLoaded || !variantId) return false;
    var info = preorderMap[variantId];
    if (!info) return false;
    return info.preorder && info.purchasable;
  }

  function isCurrentVariantPreorder() {
    return isPreorder(getSelectedVariantId());
  }

  // ── Badge Element ───────────────────────────────────────────────

  function createBadge() {
    var el = document.createElement('div');
    el.id = 'sanction-preorder-badge';
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    el.style.cssText = [
      'display: inline-flex',
      'margin: 0',
      'padding: 6px 12px',
      'background: #D97706',
      'color: #ffffff',
      'border-radius: 9999px',
      'font-size: 13px',
      'font-weight: 600',
      'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      'line-height: 1',
      'width: fit-content',
      'letter-spacing: 0.02em',
      'cursor: pointer',
    ].join('; ');
    el.textContent = 'Pre-order';

    // Only make badge clickable if the modal will be rendered (requires ship message)
    if (SHIP_MESSAGE) {
      el.addEventListener('click', function () {
        document.dispatchEvent(new CustomEvent('preorder:open-modal'));
      });
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          document.dispatchEvent(new CustomEvent('preorder:open-modal'));
        }
      });
    } else {
      el.style.cursor = 'default';
      el.removeAttribute('role');
      el.removeAttribute('tabindex');
    }

    return el;
  }

  function createShipMessage() {
    var el = document.createElement('span');
    el.id = 'sanction-preorder-ship-message';
    el.style.cssText = [
      'color: #666',
      'font-size: 13px',
      'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    ].join('; ');
    el.textContent = SHIP_MESSAGE;
    return el;
  }

  function insertBadge() {
    if (badgeElement) return;

    badgeElement = createBadge();
    shipMessageElement = createShipMessage();

    // Wrap badge + ship message in a flex row
    var wrapper = document.createElement('div');
    wrapper.id = 'sanction-preorder-wrapper';
    wrapper.style.cssText = 'display: none; align-items: center; gap: 10px; margin: 8px 0;';
    // Remove margin from badge since wrapper handles it
    badgeElement.style.margin = '0';
    wrapper.appendChild(badgeElement);
    if (SHIP_MESSAGE) {
      wrapper.appendChild(shipMessageElement);
    }

    var selectors = [
      '.option-selector',
      'product-form .product-form__buttons',
      '.product-form__buttons',
      '.product-form__cart-submit',
      '.product-single__cart-submit',
      'form[action="/cart/add"] [type="submit"]',
      'form[action*="/cart/add"] button[name="add"]',
      '.product-form',
      'form[action="/cart/add"]',
    ];

    for (var i = 0; i < selectors.length; i++) {
      var target = document.querySelector(selectors[i]);
      if (target) {
        if (selectors[i] === '.option-selector') {
          var all = document.querySelectorAll(selectors[i]);
          var last = all[all.length - 1];
          last.insertAdjacentElement('afterend', wrapper);
        } else {
          target.parentNode.insertBefore(wrapper, target);
        }
        return;
      }
    }

    document.body.appendChild(wrapper);
  }

  function updateBadge() {
    if (badgeElement && !document.contains(badgeElement)) {
      badgeElement = null;
      shipMessageElement = null;
    }
    if (!badgeElement) {
      insertBadge();
    }
    if (!badgeElement) return;

    var show = isCurrentVariantPreorder();

    // Toggle body class to suppress online-only badge via CSS when preorder
    // is active. CSS !important overrides the OO badge's inline display,
    // avoiding all script timing issues between the two badge systems.
    if (show) {
      document.body.classList.add('sanction-preorder-active');
    } else {
      document.body.classList.remove('sanction-preorder-active');
    }

    var wrapper = document.getElementById('sanction-preorder-wrapper');
    if (wrapper) {
      wrapper.style.display = show ? 'flex' : 'none';
    }
  }

  // ── ATC Button Text Change ──────────────────────────────────────

  function updateATCButton() {
    var form = document.querySelector('form.js-product-form, form[action="/cart/add"], form[action*="/cart/add"]');
    if (!form) return;

    var btn = form.querySelector('.add-to-cart, [name="add"], [type="submit"]');
    if (!btn) return;

    var show = isCurrentVariantPreorder();

    if (show) {
      // Store original text if not already stored
      if (!btn.hasAttribute('data-original-text')) {
        btn.setAttribute('data-original-text', btn.textContent.trim());
      }
      btn.textContent = PREORDER_TEXT;
    } else {
      // Restore original text
      var original = btn.getAttribute('data-original-text');
      if (original) {
        btn.textContent = original;
      }
    }
  }

  // ── Line Item Property (visible in checkout + order admin) ───────
  // The hidden input approach doesn't work reliably because many themes
  // build the /cart/add payload manually (not from FormData). Instead,
  // we intercept fetch calls to /cart/add and inject the property.

  var preorderPropertyValue = 'Yes';

  function updateLineItemProperty() {
    // No-op — property injection is handled by the fetch intercept below
  }

  // Intercept fetch to /cart/add to:
  // 1. Show confirmation dialog before adding preorder items
  // 2. Inject the Pre-order line item property
  var origFetch = window.fetch;
  var bypassConfirmation = false;

  window.fetch = function(url, options) {
    if (url && typeof url === 'string' && url.indexOf('/cart/add') !== -1 && options && options.body) {

      // Show confirmation dialog for preorder variants
      if (isCurrentVariantPreorder() && !bypassConfirmation) {
        var capturedUrl = url;
        var capturedOptions = JSON.parse(JSON.stringify(options, function(key, val) {
          if (val instanceof FormData) return '__FORMDATA__';
          return val;
        }));
        // Keep a reference to the actual options (FormData can't be cloned)
        var actualOptions = options;

        document.dispatchEvent(new CustomEvent('preorder:confirm-add', {
          detail: {
            submit: function() {
              bypassConfirmation = true;
              // Reset the button state — the never-resolving promise left it
              // disabled with a spinner. Theme checks aria-disabled and bails.
              var form = document.querySelector('form.js-product-form');
              var btn = form && form.querySelector('[name="add"], .add-to-cart');
              if (btn) {
                btn.removeAttribute('aria-disabled');
                btn.classList.remove('is-loading');
                btn.click();
              }
            }
          }
        }));

        // Return a never-resolving promise so the theme's handler stays
        // in its loading state and doesn't open the cart drawer.
        // When the user confirms, we re-trigger the button click which
        // starts a fresh fetch that goes through normally.
        return new Promise(function() {});
      }

      // Inject Pre-order line item property
      try {
        if (isCurrentVariantPreorder()) {
          var contentType = '';
          if (options.headers) {
            if (options.headers instanceof Headers) {
              contentType = options.headers.get('content-type') || '';
            } else if (typeof options.headers === 'object') {
              contentType = options.headers['Content-Type'] || options.headers['content-type'] || '';
            }
          }

          if (contentType.indexOf('application/json') !== -1 && typeof options.body === 'string') {
            var body = JSON.parse(options.body);
            if (!body.properties) body.properties = {};
            body.properties['Pre-order'] = preorderPropertyValue;
            options.body = JSON.stringify(body);
          } else if (options.body instanceof FormData) {
            options.body.set('properties[Pre-order]', preorderPropertyValue);
          }
        }
      } catch (e) {
        console.warn('[Sanction Preorder] Failed to inject property:', e);
      }

      bypassConfirmation = false;
    }
    return origFetch.apply(this, arguments);
  };

  // Also intercept XMLHttpRequest for themes that use it
  var origXHRSend = XMLHttpRequest.prototype.send;
  var origXHROpen = XMLHttpRequest.prototype.open;

  XMLHttpRequest.prototype.open = function(method, url) {
    this._sanctionUrl = url;
    return origXHROpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function(body) {
    if (this._sanctionUrl && this._sanctionUrl.indexOf('/cart/add') !== -1 && body && isCurrentVariantPreorder()) {
      try {
        if (typeof body === 'string') {
          // Try JSON
          var parsed = JSON.parse(body);
          if (!parsed.properties) parsed.properties = {};
          parsed.properties['Pre-order'] = preorderPropertyValue;
          return origXHRSend.call(this, JSON.stringify(parsed));
        }
      } catch (e) {
        // Not JSON, pass through
      }
    }
    return origXHRSend.apply(this, arguments);
  };

  // ── Dynamic Payment Buttons ─────────────────────────────────────

  function updateDynamicPaymentButtons() {
    var show = isCurrentVariantPreorder();

    var dynamicBtn = document.querySelector('.product-dynamic-payment-button, buy-buttons, .shopify-payment-button');
    if (dynamicBtn) {
      if (show) {
        dynamicBtn.setAttribute('hidden', '');
      } else {
        dynamicBtn.removeAttribute('hidden');
      }
    }
  }

  // ── Pickup Availability Override ─────────────────────────────────

  function applyPickupOverride() {
    var pickupEl = document.querySelector('pickup-availability');
    if (!pickupEl) return;

    if (isCurrentVariantPreorder()) {
      pickupEl.style.display = 'none';
    } else {
      pickupEl.style.display = '';
    }
  }

  function setupPickupObserver() {
    var container = document.querySelector('[data-dynamic-variant-content$="-pickup-availability"]');
    if (!container || pickupObserver) return;

    pickupObserver = new MutationObserver(function () {
      requestAnimationFrame(function () {
        applyPickupOverride();
      });
    });

    pickupObserver.observe(container, { childList: true, subtree: true });
  }

  // ── Variant Change Listener ─────────────────────────────────────
  // NOTE: Both online-only-badge.js and preorder-badge.js monkey-patch
  // history.pushState/replaceState. Script load order matters (defer
  // guarantees document order). Each wraps the previous wrapper, forming
  // a call chain. Both callbacks fire on every variant change.

  function watchVariantChanges() {
    var origPushState = history.pushState;
    var origReplaceState = history.replaceState;

    history.pushState = function () {
      origPushState.apply(this, arguments);
      onVariantChange();
    };

    history.replaceState = function () {
      origReplaceState.apply(this, arguments);
      onVariantChange();
    };

    window.addEventListener('popstate', onVariantChange);

    document.addEventListener(
      'change',
      function (e) {
        var target = e.target;
        if (!target) return;
        var name = (target.name || '').toLowerCase();
        var id = (target.id || '').toLowerCase();
        if (
          name.includes('variant') ||
          name.includes('option') ||
          name.includes('id') ||
          id.includes('variant') ||
          id.includes('option')
        ) {
          setTimeout(onVariantChange, 50);
        }
      },
      true
    );

    document.addEventListener(
      'click',
      function (e) {
        var target = e.target;
        if (!target) return;
        if (target.closest('.option-selector, .variant-input, [data-option], .swatch')) {
          setTimeout(onVariantChange, 100);
        }
      },
      true
    );

    document.addEventListener('shopify:section:load', function () {
      setTimeout(function () {
        insertBadge();
        onVariantChange();
      }, 100);
    });

    document.addEventListener('variant:changed', onVariantChange);
    document.addEventListener('variantChange', onVariantChange);
  }

  function onVariantChange() {
    updateBadge();
    updateATCButton();
    updateLineItemProperty();
    updateDynamicPaymentButtons();
    applyPickupOverride();
    // Retry pickup override in case Shopify re-renders the section late
    setTimeout(applyPickupOverride, 300);
    setTimeout(applyPickupOverride, 800);
  }

  // ── Storefront API Fetch ────────────────────────────────────────

  function fetchPreorderStatus() {
    var endpoint =
      'https://' +
      SANCTION_PO_CONFIG.shopDomain +
      '/api/' +
      SANCTION_PO_CONFIG.apiVersion +
      '/graphql.json';

    return fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SANCTION_PO_CONFIG.storefrontToken,
      },
      body: JSON.stringify({
        query: QUERY,
        variables: { handle: productHandle },
      }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Storefront API error: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.data || !data.data.product) {
          throw new Error('Product not found: ' + productHandle);
        }

        var variants = data.data.product.variants.edges;

        var preorderCount = 0;
        for (var i = 0; i < variants.length; i++) {
          var variant = variants[i].node;
          var varId = numericId(variant.id);

          var metafield = variant.metafield;
          var isPreorderVariant = metafield && metafield.value === 'true';

          preorderMap[varId] = {
            preorder: isPreorderVariant,
            purchasable: variant.availableForSale,
          };

          if (isPreorderVariant && variant.availableForSale) preorderCount++;
        }
        console.log('[Sanction Preorder] ' + productHandle + ': ' + preorderCount + '/' + variants.length + ' variants preorder');

        dataLoaded = true;
      });
  }

  // ── CSS rule to suppress online-only badge when preorder is active ──
  // Injected once; body class toggled per variant in updateBadge()
  var styleEl = document.createElement('style');
  styleEl.textContent = '.sanction-preorder-active #sanction-oo-badge { display: none !important; }';
  document.head.appendChild(styleEl);

  // ── Initialization ──────────────────────────────────────────────

  function init() {
    fetchPreorderStatus()
      .then(function () {
        insertBadge();
        updateBadge();
        updateATCButton();
        updateLineItemProperty();
        updateDynamicPaymentButtons();
        watchVariantChanges();
        setupPickupObserver();
        // Initial pickup override after a delay (Shopify may still be loading)
        setTimeout(applyPickupOverride, 500);
      })
      .catch(function (err) {
        console.warn('[Sanction Preorder] Failed to load preorder data:', err);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
